import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Configuration, ExpertCategories } from '../models/configuration';
import { RootState } from '../store';
import { ExpertTypeEnum } from './../../../../models/src/enums/expert/ExpertTypeEnum';

const initialState: Configuration = {
  sport_types: [],
  interests: [],
  question_answer_outcomes: [],
  knowledge_resource_types: [],
  sport_positions: [],
  sport_levels: [],
  sport_type_positions: {},
  knowledge_categories: [],
  article_categories: [],
  expert_categories: [],
  collective_categories: [],
  mentor_categories: [],
  schools: [],
  conferences: [],
  organizations: [],
  rewards: [],
  prizes: undefined,
};

const setup = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    setConfiguration(state, { payload }: PayloadAction<Configuration>) {
      return { ...state, ...payload };
    },
    setConfigurationSports(
      state,
      { payload }: PayloadAction<Configuration['sport_types']>
    ) {
      return { ...state, sport_types: payload };
    },
    setConfigurationInterests(
      state,
      { payload }: PayloadAction<Configuration['interests']>
    ) {
      return {
        ...state,
        interests: payload,
      };
    },
    setConfigurationQuestionAnswerOutcomes(
      state,
      { payload }: PayloadAction<Configuration['question_answer_outcomes']>
    ) {
      return {
        ...state,
        question_answer_outcomes: payload,
      };
    },
    setConfigurationKnowledgeResourceTypes(
      state,
      { payload }: PayloadAction<Configuration['knowledge_resource_types']>
    ) {
      return {
        ...state,
        knowledge_resource_types: payload,
      };
    },
    setConfigurationSportPositions(
      state,
      { payload }: PayloadAction<Configuration['sport_positions']>
    ) {
      return {
        ...state,
        sport_positions: payload,
      };
    },
    setConfigurationSportLevels(
      state,
      { payload }: PayloadAction<Configuration['sport_levels']>
    ) {
      return {
        ...state,
        sport_levels: payload,
      };
    },
    setConfigurationSportTypePositions(
      state,
      { payload }: PayloadAction<Configuration['sport_type_positions']>
    ) {
      return {
        ...state,
        sport_type_positions: payload,
      };
    },
    setConfigurationKnowledgeCategories(
      state,
      { payload }: PayloadAction<Configuration['knowledge_categories']>
    ) {
      return {
        ...state,
        knowledge_categories: payload,
      };
    },
    setConfigurationArticlesCategories(
      state,
      { payload }: PayloadAction<Configuration['article_categories']>
    ) {
      return {
        ...state,
        article_categories: payload,
      };
    },
    setConfigurationExpertCategories(
      state,
      { payload }: PayloadAction<ExpertCategories[]>
    ) {
      return {
        ...state,
        expert_categories: payload.filter(
          (item) => item.expert_type === ExpertTypeEnum.Expert
        ),
        collective_categories: payload.filter(
          (item) => item.expert_type === ExpertTypeEnum.AgencyOrCollective
        ),
        mentor_categories: payload.filter(
          (item) => item.expert_type === ExpertTypeEnum.Mentors
        ),
      };
    },
    setConfigurationSchools(
      state,
      { payload }: PayloadAction<Configuration['schools']>
    ) {
      return {
        ...state,
        schools: payload,
      };
    },
    setConfigurationConferences(
      state,
      { payload }: PayloadAction<Configuration['conferences']>
    ) {
      return {
        ...state,
        conferences: payload,
      };
    },
    setConfigurationOrganizations(
      state,
      { payload }: PayloadAction<Configuration['organizations']>
    ) {
      return {
        ...state,
        organizations: payload,
      };
    },
    setConfigurationRewards(
      state,
      { payload }: PayloadAction<Configuration['rewards']>
    ) {
      return {
        ...state,
        rewards: payload,
      };
    },
    setConfigurationPrizes(
      state,
      { payload }: PayloadAction<Configuration['prizes']>
    ) {
      return {
        ...state,
        prizes: payload,
      };
    },
    /**
     * @deprecated This not should called because this reducer stores only mocked data from server, so if we call it we should to restore all reducer
     */
    clearConfiguration() {
      return initialState;
    },
  },
});

export const {
  setConfiguration,
  setConfigurationSports,
  setConfigurationSportLevels,
  setConfigurationSportPositions,
  setConfigurationKnowledgeCategories,
  setConfigurationArticlesCategories,
  setConfigurationExpertCategories,
  setConfigurationSchools,
  setConfigurationConferences,
  setConfigurationInterests,
  setConfigurationOrganizations,
  setConfigurationRewards,
  setConfigurationPrizes,
} = setup.actions;

export const selectConfiguration = ({ configuration }: RootState) =>
  configuration;
export const selectConfigurationSports = ({ configuration }: RootState) =>
  configuration.sport_types;
export const selectConfigurationInterests = ({ configuration }: RootState) =>
  configuration.interests;
export const selectConfigurationQuestionAnswerOutcomes = ({
  configuration,
}: RootState) => configuration.question_answer_outcomes;
export const selectConfigurationKnowledgeResourceTypes = ({
  configuration,
}: RootState) => configuration.knowledge_resource_types;
export const selectConfigurationSportPositions = ({
  configuration,
}: RootState) => configuration.sport_positions;
export const selectConfigurationSportLevels = ({ configuration }: RootState) =>
  configuration.sport_levels;
export const selectConfigurationSportTypePositions = ({
  configuration,
}: RootState) => configuration.sport_type_positions;
export const selectConfigurationKnowledgeCategories = ({
  configuration,
}: RootState) => configuration.knowledge_categories;
export const selectConfigurationArticlesCategories = ({
  configuration,
}: RootState) => configuration.article_categories;
export const selectConfigurationExpertCategories = ({
  configuration,
}: RootState) => configuration.expert_categories;
export const selectConfigurationCollectiveCategories = ({
  configuration,
}: RootState) => configuration.collective_categories;
export const selectConfigurationMentorCategories = ({
  configuration,
}: RootState) => configuration.mentor_categories;
export const selectConfigurationSchools = ({ configuration }: RootState) =>
  configuration.schools;
export const selectConfigurationConferences = ({ configuration }: RootState) =>
  configuration.conferences;
export const selectConfigurationOrganizations = ({
  configuration,
}: RootState) => configuration.organizations;
export const selectConfigurationRewards = ({ configuration }: RootState) =>
  configuration.rewards;
export const selectConfigurationPrizes = ({ configuration }: RootState) =>
  configuration.prizes;

export default setup.reducer;
